/**
 * 入驻机构页面组件 - 从数据维护模块获取真实数据
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Tag, Spin, message, Tooltip } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { institutionService } from '../../../services/institutionService';
import { useDict } from '../../../hooks/useDict';
import { DICT_TYPES } from '../../../constants/dictTypes';
import './ShowcaseWebsite.css';

interface Institution {
  id: number;
  name: string;
  type: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  description: string;
  website?: string;
  logo?: string;
  status: number;
  createTime: string;
}

const InstitutionPage: React.FC = () => {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInstitutionType, setSelectedInstitutionType] = useState('全部');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  
  // 使用数据字典获取机构类型（从API获取，替代硬编码）
  const { dictData: institutionTypeDict, getLabelByValue } = useDict(DICT_TYPES.INSTITUTION_TYPE);

  // 从字典动态生成机构类型分类配置
  const institutionTypes = React.useMemo(() => {
    const types = [{ key: '全部', label: '全部', icon: '🏆', color: '#1890ff' }];
    
    // 从字典数据生成类型选项
    if (institutionTypeDict && institutionTypeDict.length > 0) {
      const colors = ['#52c41a', '#fa8c16', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];
      const icons = ['📋', '⚖️', '📊', '🏦', '🛡️', '🎓'];
      
      institutionTypeDict.forEach((item, index) => {
        if (item.dictValue) {
          types.push({
            key: item.dictValue,
            label: item.dictLabel || item.dictValue,
            icon: icons[index % icons.length],
            color: colors[index % colors.length]
          });
        }
      });
    } else {
      // 如果字典数据未加载，使用默认配置（临时）
      const defaultTypes = [
        { key: 'agency', label: '代理机构', icon: '📋', color: '#52c41a' },
        { key: 'legal_consultation', label: '法律咨询', icon: '⚖️', color: '#fa8c16' },
        { key: 'evaluation', label: '评估机构', icon: '📊', color: '#722ed1' },
        { key: 'financial', label: '金融机构', icon: '🏦', color: '#13c2c2' },
        { key: 'insurance', label: '保险机构', icon: '🛡️', color: '#eb2f96' },
        { key: 'university_research', label: '高校、科研院所', icon: '🎓', color: '#fa541c' },
      ];
      types.push(...defaultTypes);
    }
    
    return types;
  }, [institutionTypeDict]);

  // 组件挂载时加载数据 - 使用 useRef 防止重复调用
  const loadingRef = React.useRef(false);
  
  useEffect(() => {
    let isMounted = true;
    
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }
    
    const loadInstitutions = async () => {
      // 设置加载标志，防止重复调用
      loadingRef.current = true;
      setLoading(true);
      
      try {
        const response = await institutionService.getList({
          current: 1,
          size: 1000,
          status: 1 // 只获取正常状态的机构
        });
        
        if (isMounted) {
          if (response.data && response.data.records) {
            setInstitutions(response.data.records);
          } else {
            setInstitutions([]);
          }
        }
      } catch (error) {
        console.error('加载机构数据失败:', error);
        if (isMounted) {
          message.error('加载机构数据失败，请稍后重试');
          setInstitutions([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    };
    
    loadInstitutions();
    
    return () => {
      isMounted = false;
      loadingRef.current = false;
    };
  }, []);

  // 筛选机构
  const filteredInstitutions = selectedInstitutionType === '全部'
    ? institutions
    : institutions.filter(item => item.type === selectedInstitutionType);

  // 获取机构类型的中文名称
  const getInstitutionTypeLabel = (typeKey: string) => {
    return getLabelByValue(typeKey) || typeKey;
  };

  // 获取类型配置
  const getTypeConfig = (type: string) => {
    const config = institutionTypes.find(t => t.key === type);
    return config || { key: type, label: type, icon: '📋', color: '#1890ff' };
  };

  return (
    <div className="institutions-page">
      {/* 页面头部横幅 */}
      <div className="institutions-banner">
        <div className="banner-content">
          <div className="banner-text">
            <h1 className="banner-title">入驻机构</h1>
            <p className="banner-subtitle">汇聚专业的知识产权服务机构，为您提供全方位的知识产权服务</p>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="institutions-content">
        {/* 分类筛选 */}
        <div className="filters-section">
          <div className="type-filters">
            {institutionTypes.map(type => {
              const isActive = selectedInstitutionType === type.key;
              const count = type.key === '全部' 
                ? institutions.length 
                : institutions.filter(item => item.type === type.key).length;
              
              return (
                <button
                  key={type.key}
                  className={`filter-btn ${isActive ? 'ant-btn-primary' : ''}`}
                  onClick={() => setSelectedInstitutionType(type.key)}
                  style={{ '--btn-color': type.color } as React.CSSProperties}
                >
                  <span className="btn-icon">{type.icon}</span>
                  <span className="btn-text">{type.label}</span>
                  {type.key !== '全部' && (
                    <span className="count-badge">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 机构展示区域 */}
        <div className="institutions-grid">
          {/* 标题区域 */}
          <div className="institutions-header">
            <h2 className="institutions-title">
              {selectedInstitutionType === '全部' ? '全部机构' : getTypeConfig(selectedInstitutionType).label}
            </h2>
            <p className="institutions-count">
              ({filteredInstitutions.length}项)
            </p>
          </div>

          {/* 机构列表 */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spin size="large" />
              <p style={{ marginTop: '1rem', color: '#666' }}>正在加载机构数据...</p>
            </div>
          ) : (
            <Row gutter={[24, 24]}>
              {filteredInstitutions.map(item => {
                const typeConfig = getTypeConfig(item.type);
                return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={item.id}>
                    <Card
                      hoverable
                      className="institution-card"
                      data-type={item.type}
                      onClick={() => navigate(`/show/institutions/${item.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="institution-logo">
                        {item.logo && !failedImages.has(item.logo) ? (
                          <img 
                            src={(() => {
                              // 处理图片路径：如果是相对路径，确保以/开头；如果是完整URL或Base64，直接使用
                              if (!item.logo) return '';
                              // Base64或完整URL，直接使用
                              if (item.logo.startsWith('data:') || item.logo.startsWith('http://') || item.logo.startsWith('https://')) {
                                return item.logo;
                              }
                              // 相对路径，确保以/开头，这样React会从public目录加载
                              return item.logo.startsWith('/') ? item.logo : '/' + item.logo;
                            })()}
                            alt={item.name}
                            onError={() => {
                              // 图片加载失败时，记录到failedImages集合
                              setFailedImages(prev => new Set(prev).add(item.logo!));
                            }}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'contain',
                              borderRadius: '8px'
                            }}
                          />
                        ) : (
                          <BankOutlined className="default-logo" />
                        )}
                      </div>
                      <Card.Meta
                        title={item.name}
                        description={
                          <Tooltip title={item.description || '暂无描述'} placement="top">
                            <div className="institution-description-text">
                              {item.description || '暂无描述'}
                            </div>
                          </Tooltip>
                        }
                      />
                      <div className="institution-type-tag">
                        <Tag color={typeConfig.color}>{typeConfig.label}</Tag>
                      </div>
                      {item.website && (
                        <div className="institution-website">
                          <a 
                            href={item.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#1890ff', fontSize: '12px' }}
                          >
                            访问官网
                          </a>
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* 空状态 */}
          {!loading && filteredInstitutions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <BankOutlined style={{ fontSize: '48px', marginBottom: '1rem' }} />
              <p>暂无机构数据</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstitutionPage;
